import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const HERO_IMG = "/assets/hero.png";

export default function Landing() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen">
      <Brand />
      <main className="mx-auto max-w-7xl px-6 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="md:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" /> {t("hero_kicker")}
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              {t("hero_title")}
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed">
              {t("hero_sub")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/assessment" data-testid="cta-start-hero">
                <Button size="lg" className="rounded-full px-8 h-12 text-sm font-semibold tracking-wide">
                  {t("cta_start")} →
                </Button>
              </Link>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-foreground/50"/>{t("trust_1")}</span>
                <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-foreground/50"/>{t("trust_2")}</span>
                <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-foreground/50"/>{t("trust_3")}</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-5 relative">
            <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-b from-primary/10 via-card to-card">
              <img src={HERO_IMG} alt="ERP Institute Karriereberatung" width="940" height="1200"
                   loading="eager" fetchpriority="high"
                   className="w-full h-[520px] object-contain object-bottom" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-primary/15 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 glass rounded-xl p-4">
                <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Sample match</div>
                <div className="mt-1 text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>SAP FI Key User</div>
                <div className="text-xs text-muted-foreground">92% match · Confidence: sehr hoch</div>
              </div>
            </div>
          </motion.div>
        </div>

        <section className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { t: t("feature_1_t"), d: t("feature_1_d") },
            { t: t("feature_2_t"), d: t("feature_2_d") },
            { t: t("feature_3_t"), d: t("feature_3_d") },
          ].map((f, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="rounded-xl border border-border bg-card p-6 hover:-translate-y-1 transition-transform"
              data-testid={`feature-card-${i}`}>
              <div className="text-[10px] uppercase tracking-[0.24em] text-accent">0{i + 1}</div>
              <h3 className="mt-3 text-lg font-semibold">{f.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </section>
      </main>
      <footer className="border-t border-border/60 mt-16">
        <div className="mx-auto max-w-7xl px-6 py-6 text-xs text-muted-foreground flex justify-between">
          <span>© {new Date().getFullYear()} ERP Institute</span>
          <span className="tracking-[0.2em] uppercase">career.erp-institute.net</span>
        </div>
      </footer>
    </div>
  );
}
